import {
	ActionButton,
	FilterChip,
	ListPageChartCard,
	ListPageCharts,
	ListPageLayout,
	Pagination,
	SectionCard,
	EmptyState,
} from '@practium/ui';
import type { SectionTab } from '@practium/ui';
import { useEffect, useState } from 'react';
import { SystemPageSkeleton } from '../components/Skeletons/SystemPageSkeleton';
import { PiePercentageChart, ComparisonLineChart } from '@practium/features/dashboard';
import { GroupsTable } from '../components/Tables/GroupsTable/GroupsTable';
import { HierarchiesTable } from '../components/Tables/HierarchiesTable/HierarchiesTable';
import { PlansTable } from '../components/Tables/PlansTable/PlansTable';
import type { UseSystemPageProps } from '../hooks/useSystemPage';
import { useSystemPage } from '../hooks/useSystemPage';
import type { SystemTab } from '../types';
import {
	USAGE_PIE_DATA,
	USERS_HIERARCHY_PIE_DATA,
	SUBSCRIBERS_PIE_DATA,
	SUBSCRIBERS_LINE_DATA,
} from '../constants/charts.constants';
import {
	SYSTEM_SECTION_TITLES,
	SYSTEM_SECTION_SUBTITLES,
	SYSTEM_PIE_TITLES,
	SYSTEM_PIE_SUBTITLES,
} from '../constants/page.constants';

const PIE_DATA: Record<SystemTab, { name: string; value: number; color: string }[]> = {
	permissions: USAGE_PIE_DATA,
	hierarchies: USERS_HIERARCHY_PIE_DATA,
	plans: SUBSCRIBERS_PIE_DATA,
};

export interface SystemPageProps extends UseSystemPageProps {
	onAddNew?: (tab: SystemTab) => void;
	onViewItem?: (tab: SystemTab, id: string) => void;
	onReportClick?: () => void;
	canCreateForTab?: (tab: SystemTab) => boolean;
}

export const SystemPage = ({
	onAddNew,
	onViewItem,
	onReportClick,
	canCreateForTab,
	...hookProps
}: SystemPageProps) => {
	const {
		activeTab,
		setActiveTab,
		hierarchies,
		filteredGroups,
		filteredHierarchies,
		filteredPlans,
		searchQuery,
		setSearchQuery,
		sortAsc,
		toggleSort,
		filterValues,
		setFilterValues,
		filterGroups,
		page,
		setPage,
		totalPages,
		isTableLoading,
		isTableFetching,
		isTableError,
		refetchTable,
	} = useSystemPage(hookProps);
	const [hasRenderedContent, setHasRenderedContent] = useState(false);
	const isInitialLoading = !hasRenderedContent && isTableLoading && !isTableError;

	useEffect(() => {
		if (!isInitialLoading) setHasRenderedContent(true);
	}, [isInitialLoading]);

	if (isInitialLoading) return <SystemPageSkeleton />;

	const tableError = isTableError ? (
		<div role="alert">
			<EmptyState
				title="Não foi possível carregar os dados do sistema"
				description="Tente novamente para carregar os dados."
			/>
			<ActionButton text="Tentar novamente" onClick={() => void refetchTable()} />
		</div>
	) : null;

	const handleReport = () => onReportClick?.();
	const canAddNew = canCreateForTab ? canCreateForTab(activeTab) : true;

	const tabs: SectionTab<SystemTab>[] = [
		{
			key: 'permissions',
			label: 'Permissões',
			content: (
				<GroupsTable
					groups={filteredGroups}
					onRowClick={onViewItem ? id => onViewItem('permissions', id) : undefined}
				/>
			),
		},
		{
			key: 'hierarchies',
			label: 'Hierarquias',
			content: (
				<HierarchiesTable
					hierarchies={filteredHierarchies}
					onRowClick={onViewItem ? id => onViewItem('hierarchies', id) : undefined}
				/>
			),
		},
		{
			key: 'plans',
			label: 'Planos',
			content: (
				<PlansTable
					plans={filteredPlans}
					hierarchies={hierarchies}
					onRowClick={onViewItem ? id => onViewItem('plans', id) : undefined}
				/>
			),
		},
	];

	return (
		<ListPageLayout
			sidebarWidth="28rem"
			sidebarW1500="22rem"
			sidebarW1300="19rem"
			sidebarW1200="17rem"
			sidebar={
				<ListPageCharts single={activeTab !== 'plans'}>
					<ListPageChartCard maxHeight="30rem">
						<PiePercentageChart
							title={SYSTEM_PIE_TITLES[activeTab]}
							subtitle={SYSTEM_PIE_SUBTITLES[activeTab]}
							buttonText="Ver Relatório"
							onButtonClick={handleReport}
							data={PIE_DATA[activeTab]}
						/>
					</ListPageChartCard>
					{activeTab === 'plans' && (
						<ListPageChartCard>
							<ComparisonLineChart
								title="Assinantes na Plataforma"
								mainValue="31 Novos Assinantes"
								trendValue="12.3%"
								trendLabel="vs semana anterior"
								trendDirection="down"
								footerText="Assinantes de 1–6 Dez, 2025"
								buttonText="Ver Relatório"
								onButtonClick={handleReport}
								data={SUBSCRIBERS_LINE_DATA}
								xAxisKey="day"
								line1Key="atual"
								line1Label="Semana atual"
								line2Key="anterior"
								line2Label="Semana anterior"
								tooltipTitle="Assinantes"
							/>
						</ListPageChartCard>
					)}
				</ListPageCharts>
			}
		>
			<SectionCard
				title={SYSTEM_SECTION_TITLES[activeTab]}
				subtitle={SYSTEM_SECTION_SUBTITLES[activeTab]}
				tabs={tabs.map((tab) => ({ ...tab, content: tableError ?? tab.content }))}
				isLoading={isTableLoading && !isTableError}
				isFetching={isTableFetching && !isTableError}
				loadingLabel="Carregando dados do sistema"
				activeTab={activeTab}
				onTabChange={setActiveTab}
				search={true}
				searchQuery={searchQuery}
				onSearch={setSearchQuery}
				sortAsc={sortAsc}
				onToggleSort={toggleSort}
				filterSlot={
					filterGroups.length > 0 ? (
						<FilterChip
							values={filterValues}
							onChange={setFilterValues}
							groups={filterGroups}
							placeholder="Filtros"
						/>
					) : undefined
				}
				footer={<Pagination page={page} totalPages={totalPages} onPageChange={setPage} />}
				headerAction={
					<>
						{canAddNew && <ActionButton text="Adicionar" onClick={() => onAddNew?.(activeTab)} />}
						<ActionButton text="Ver Relatório" onClick={handleReport} />
					</>
				}
			/>
		</ListPageLayout>
	);
};
